import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    createFirebaseUser,
    firebaseAuth,
    loginFirebaseUser,
    signInWithGoogle as signInWithGooglePopUp,
    signInWithMsfPopUp,
} from '../firebase';
import {
    onAuthStateChanged,
    User as FirebaseUser,
    linkWithCredential,
    OAuthCredential,
    unlink,
} from 'firebase/auth';
import * as api from '../api/services';
import { Brand, Plan, User } from '../types/types';
import Api from '@/api/client';
import { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';
import * as Sentry from '@sentry/react';
import {
    ACCENT_COLOR,
    LINKEDIN_QUERY_PARAM,
    LINKEDIN_STORAGE_KEY,
    PRIMARY_COLOR,
} from '@/constants';
import {
    GCLID_QUERY_PARAM,
    GCLID_STORAGE_KEY,
    PARTNERSTACK_XID_QUERY_PARAM,
    PARTNERSTACK_XID_STORAGE_KEY,
} from '@/constants';

export interface AuthContextValue {
    user: User | null;
    plan: Plan;
    isSuperUser: boolean;
    signInWithGoogle: (params?: { isApiDev?: boolean }) => Promise<void>;
    signInWithMSF: (params?: { isApiDev?: boolean }) => Promise<void>;
    signOut: () => Promise<void>;
    hasLoadedUser: boolean;
    setUser: Dispatch<SetStateAction<User | null>>;
    signUpWithEmailPass: (user: SignUpWithEmailParams) => Promise<void>;
    signInWithEmailPass: (email: string, password: string) => Promise<void>;
    // resetPassword: (email: string) => Promise<void>;
    syncUser: () => Promise<void>;
    initLocalUserFromFbUser: (firebaseUser: FirebaseUser) => Promise<void>;
    brand?: Brand | null;
    setBrand: Dispatch<SetStateAction<Brand | null>>;
}

export interface SignUpWithEmailParams {
    email: string;
    password: string;
    name: string;
    isApiDev?: boolean;
    campaign?: 'appsumo';
    appsumoCode?: string;
}

const defaultValue: Partial<AuthContextValue> = {
    user: null,
    plan: 'FREE',
    hasLoadedUser: false,
};

// @ts-expect-error
const AuthContext = React.createContext<AuthContextValue>(defaultValue);

// returned by createUser when the email already belongs to an account on the other brand
export const isEmailAlreadyUsedError = (error: unknown) =>
    error instanceof AxiosError &&
    (error.response?.data as { message?: string } | undefined)?.message ===
        'EMAIL_ALREADY_USED';

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [hasLoadedUser, setHasLoadedUser] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [plan, setPlan] = useState<Plan>('FREE');
    const [isSuperUser, setIsSuperUser] = useState(false);
    const [brand, setBrand] = useState<Brand | null>(null);

    const hasCheckedUser = useRef(false);
    const broadcastChannel = useRef<BroadcastChannel | null>(null);

    const getStoredPartnerStackXid = () => {
        if (typeof window === 'undefined') return;
        const xid = window.localStorage.getItem(PARTNERSTACK_XID_STORAGE_KEY);
        return xid?.trim() || undefined;
    };

    const getStoredGclid = () => {
        if (typeof window === 'undefined') return;
        const gclid = window.localStorage.getItem(GCLID_STORAGE_KEY);
        return gclid?.trim() || undefined;
    };

    const getStoredLiFatId = () => {
        if (typeof window === 'undefined') return;
        const gclid = window.localStorage.getItem(LINKEDIN_STORAGE_KEY);
        return gclid?.trim() || undefined;
    };

    const getAdConsent = () => {
        const consent = (window as any).getCkyConsent?.();
        return Boolean(
            consent?.isUserActionCompleted &&
                consent.categories?.advertisement
        );
    };

    const getAdTrackingParams = () => {
        const adConsent = getAdConsent();
        return {
            adConsent,
            ...(adConsent && {
                gclid: getStoredGclid(),
                liFatId: getStoredLiFatId(),
            }),
        };
    };
    // const { reset } = useRequestContext();

    // used to detect user at initial page load (or refresh)
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(
            firebaseAuth,
            async (firebaseUser) => {
                if (hasCheckedUser.current) return;
                hasCheckedUser.current = true;

                if (firebaseUser) {
                    try {
                        await Api.init(firebaseUser);
                        const { data: user } = await api.getUser();
                        // Api.setSuperUserKey(user.superUserKey!);
                        setUser(user);
                        setBrand(user.brand);
                        Sentry.setUser({
                            email: user.email,
                            id: user.id,
                            username: user.name,
                        });
                        setPlan(user.plan);
                        setIsSuperUser(Boolean(user?.features.SUPER));
                    } catch (error) {
                        Sentry.captureException(error);
                        signOut();
                    }
                }
                setHasLoadedUser(true);
            }
        );
        return unsubscribe;
    }, []);

    useEffect(() => {
        document.documentElement.style.setProperty(
            '--main-color',
            brand?.color || PRIMARY_COLOR
        );
    }, [brand?.color]);

    useEffect(() => {
        document.documentElement.style.setProperty(
            '--accent-color',
            brand?.accent || ACCENT_COLOR
        );
    }, [brand?.accent]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const xid = params.get(PARTNERSTACK_XID_QUERY_PARAM)?.trim();
        if (!xid) return;

        window.localStorage.setItem(PARTNERSTACK_XID_STORAGE_KEY, xid);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const gclid = params.get(GCLID_QUERY_PARAM)?.trim();
        if (!gclid) return;

        window.localStorage.setItem(GCLID_STORAGE_KEY, gclid);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const gclid = params.get(LINKEDIN_QUERY_PARAM)?.trim();
        if (!gclid) return;

        window.localStorage.setItem(LINKEDIN_STORAGE_KEY, gclid);
    }, []);

    const signInWithGoogle = async ({
        isApiDev,
    }: { isApiDev?: boolean } = {}) => {
        let firebaseUserCopy;
        // https://github.com/firebase/firebase-js-sdk/issues/8061#issuecomment-2047370693
        const originalSetTimeout = window.setTimeout;
        //@ts-ignore
        window.setTimeout = function (fn, delay, ...args) {
            // Check if the delay matches Firebase's _Timeout.AUTH_EVENT
            if (delay === 8000) {
                delay = 100;
            }
            return originalSetTimeout(fn, delay, ...args);
        };

        try {
            const { user: firebaseUser } = await signInWithGooglePopUp();
            firebaseUserCopy = firebaseUser;
            await Api.init(firebaseUser);
            const { data: user } = await api.getUser();
            setUser(user);
            setBrand(user.brand);
            Sentry.setUser({
                email: user.email,
                id: user.id,
                username: user.name,
            });
            setPlan(user.plan);
            setIsSuperUser(Boolean(user?.features.SUPER));
        } catch (error) {
            if (
                error instanceof FirebaseError &&
                error.code === 'auth/popup-closed-by-user'
            ) {
                return;
            }

            if (error instanceof AxiosError && error.response?.status === 404) {
                try {
                    const { data: user } = await api.createUser({
                        name: firebaseUserCopy?.displayName!,
                        isApiDev,
                        partnerstackXid: getStoredPartnerStackXid(),
                        ...getAdTrackingParams(),
                    });
                    setUser(user);
                    setBrand(user.brand);
                    Sentry.setUser({
                        email: user.email,
                        id: user.id,
                        username: user.name,
                    });
                    return;
                } catch (error) {
                    signOut();
                    throw error;
                }
            }

            signOut();
            throw error;
        } finally {
            window.setTimeout = originalSetTimeout;
        }
    };

    const signInWithMSF = async ({ isApiDev }: { isApiDev?: boolean } = {}) => {
        let firebaseUserCopy;
        // https://github.com/firebase/firebase-js-sdk/issues/8061#issuecomment-2047370693
        const originalSetTimeout = window.setTimeout;
        //@ts-ignore
        window.setTimeout = function (fn, delay, ...args) {
            // Check if the delay matches Firebase's _Timeout.AUTH_EVENT
            if (delay === 8000) {
                delay = 100;
            }
            return originalSetTimeout(fn, delay, ...args);
        };

        try {
            const { user: firebaseUser } = await signInWithMsfPopUp();
            firebaseUserCopy = firebaseUser;
            await Api.init(firebaseUser);
            const { data: user } = await api.getUser();
            setUser(user);
            setBrand(user.brand);
            Sentry.setUser({
                email: user.email,
                id: user.id,
                username: user.name,
            });
            setPlan(user.plan);
            setIsSuperUser(Boolean(user?.features.SUPER));
        } catch (error) {
            if (
                error instanceof FirebaseError &&
                error.code === 'auth/popup-closed-by-user'
            ) {
                return;
            }

            if (error instanceof AxiosError && error.response?.status === 404) {
                try {
                    const { data: user } = await api.createUser({
                        name: firebaseUserCopy?.displayName!,
                        isApiDev,
                        partnerstackXid: getStoredPartnerStackXid(),
                        ...getAdTrackingParams(),
                    });
                    setUser(user);
                    setBrand(user.brand);
                    Sentry.setUser({
                        email: user.email,
                        id: user.id,
                        username: user.name,
                    });
                    return;
                } catch (error) {
                    signOut();
                    throw error;
                }
            }

            signOut();
            throw error;
        } finally {
            window.setTimeout = originalSetTimeout;
        }
    };

    const signUpWithEmailPass = async ({
        name,
        email,
        password,
        isApiDev,
        campaign,
        appsumoCode,
    }: SignUpWithEmailParams) => {
        try {
            const { user: firebaseUser } = await createFirebaseUser(
                email,
                password
            );
            await Api.init(firebaseUser);
            const { data: user } = await api.createUser({
                name,
                isApiDev,
                campaign,
                appsumoCode,
                partnerstackXid: getStoredPartnerStackXid(),
                ...getAdTrackingParams(),
            });
            setUser(user);
            setBrand(user.brand);
            Sentry.setUser({
                email: user.email,
                id: user.id,
                username: user.name,
            });
            setPlan(user.plan);
            setIsSuperUser(false);
        } catch (error) {
            await signOut();
            throw error;
        }
    };

    const initLocalUserFromFbUser = async (firebaseUser: FirebaseUser) => {
        try {
            await Api.init(firebaseUser);
            const { data: user } = await api.getUser();
            // Api.setSuperUserKey(user.superUserKey!);
            setUser(user);
            setBrand(user.brand);
            Sentry.setUser({
                email: user.email,
                id: user.id,
                username: user.name,
            });
            setPlan(user.plan);
            setIsSuperUser(Boolean(user?.features.SUPER));
        } catch (error) {
            signOut();
            throw error;
            //  handleError
        }
    };

    const signInWithEmailPass = async (email: string, password: string) => {
        try {
            const { user: firebaseUser } = await loginFirebaseUser(
                email,
                password
            );

            // logic to switch from email/pass login to MSF when a user tries to authenticate with MSF but already has an account
            //@ts-ignore
            if (window.MSF_FIREBASE_CREDENTIAL instanceof OAuthCredential) {
                const { user } = await linkWithCredential(
                    firebaseUser,
                    //@ts-ignore
                    window.MSF_FIREBASE_CREDENTIAL
                );
                const user2 = await unlink(user, 'password');
                //@ts-ignore
                window.MSF_FIREBASE_CREDENTIAL = undefined;
                alert(
                    'Your Microsoft account has been enabled. Loging in with username and password in no longer possible.'
                );
                await initLocalUserFromFbUser(user2);
            } else {
                await initLocalUserFromFbUser(firebaseUser);
            }
        } catch (error) {
            signOut();
            throw error;
            //  handleError
        }
    };

    const syncUser = useCallback(async () => {
        const { data: user } = await api.getUser();
        setUser(user);
        setBrand(user.brand);
        setPlan(user.plan);
        setIsSuperUser(Boolean(user?.features.SUPER));
    }, []);

    // Listen for signout events from other tabs to sync signout across tabs (used by change email flow and regular signout)
    useEffect(() => {
        broadcastChannel.current = new BroadcastChannel('auth');
        broadcastChannel.current.onmessage = (e) => {
            if (e.data === 'signout') {
                signOut(false);
            }
        };
        return () => {
            broadcastChannel.current?.close();
        };
    }, []);

    const signOut = async (withBroadcast = true) => {
        if (withBroadcast) {
            broadcastChannel.current?.postMessage('signout');
        }
        await firebaseAuth.signOut();
        Api.signOut();
        setUser(null);
        Sentry.setUser(null);
        setPlan('FREE');
        setIsSuperUser(false);
        // setBrand(null);
        // reset();
    };

    // const resetPassword = (email: string) => {
    //     return sendFbPasswordResetEmail(email);
    // };

    const value = {
        user,
        signInWithGoogle,
        signInWithMSF,
        signOut,
        hasLoadedUser,
        signUpWithEmailPass,
        signInWithEmailPass,
        // resetPassword,
        setUser,
        plan,
        isSuperUser,
        syncUser,
        initLocalUserFromFbUser,
        brand,
        setBrand,
    };

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = React.useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within a AuthProvider');
    }
    return context;
};
